import { useTranslation } from "react-i18next";

import { Pagination } from "@mui/material";
import {
  gridPageCountSelector,
  gridPaginationModelSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid";

/**
 * Numbered page control rendered in the footer of every paginated list view.
 *
 * The free (Community) DataGrid shows at most DATA_TABLE_PAGE_SIZE rows per page, so everything past
 * that mark is unreachable without a control; its own footer offers only previous/next, which never
 * reveals how many pages exist. Renders nothing while the whole list fits on one page, so short
 * lists keep their footer-less layout.
 */
export function DataTablePagination() {
  const { t } = useTranslation();
  const apiRef = useGridApiContext();
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);
  const { page } = useGridSelector(apiRef, gridPaginationModelSelector);

  if (pageCount <= 1) {
    return null;
  }

  return (
    <Pagination
      count={pageCount}
      page={page + 1}
      onChange={(_, nextPage) => apiRef.current.setPage(nextPage - 1)}
      color="primary"
      shape="rounded"
      aria-label={t("common.pagination.label")}
      getItemAriaLabel={(type, itemPage) => {
        switch (type) {
          case "previous":
            return t("common.pagination.previousPage");
          case "next":
            return t("common.pagination.nextPage");
          case "page":
            return t("common.pagination.page", { page: itemPage });
          default:
            // The remaining types are the decorative ellipses; MUI never renders a label for them.
            return "";
        }
      }}
      sx={{ display: "flex", justifyContent: "center", py: 1.5, borderTop: 1, borderColor: "divider" }}
    />
  );
}
